"use client";

import { FormInput } from "@boostdev/design-system-components/client";
import { Button } from "@/components/atoms/Button/Button";
import { useClientTranslation } from "@/lib/i18n/client";
import { useTransition } from "react";
import css from "./page.module.css";
import Image from "next/image";
import { cn } from "@boostdev/design-system-components/utils";
import { useRouter } from "next/navigation";
import type { Language } from "@/lib/i18n/config";

export interface FormProps {
  readonly locale: Language;
  /** True when we already tried a code via the URL and it didn't resolve. */
  readonly showInvalidCodeMessage?: boolean;
}

export const Form = ({ locale, showInvalidCodeMessage = false }: FormProps) => {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const { t } = useClientTranslation();

  const handleFormSubmit = (formData: FormData) => {
    const code = String(formData.get("code") ?? "").trim();
    if (!code) return;
    const target = `/${locale}/photo-code/${encodeURIComponent(code)}`;
    startTransition(() => router.push(target));
  };

  return (
    <form className={cn(css.form)} action={handleFormSubmit}>
      <div className={css.formField}>
        <FormInput
          className={css.formInput}
          label={t("common:photoCode.form.input.label")}
          type="text"
          maxLength={32}
          name="code"
          inputMode="text"
          pattern="[A-Za-z0-9_-]{4,32}"
          placeholder={t("common:photoCode.form.input.placeholder")}
        />
        <Button
          variant="secondary"
          type="submit"
          disabled={pending}
          className={css.submit}
        >
          {t("common:photoCode.form.cta")}
        </Button>
        {showInvalidCodeMessage ? (
          <div className={css.resultContainer}>
            <p>{t("common:photoCode.form.invalidCode")}</p>
          </div>
        ) : null}
      </div>
      <Image
        className={css.image}
        src="/assets/fotobjorn.jpg"
        alt="Bjorn Boonen"
        width={200}
        height={200}
      />
    </form>
  );
};
